'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Check } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

interface RoleCardProps {
  id: string;
  role: string;
  subtitle: string;
  description: string;
  href: string;
  iconName: string;
  accentColor: 'primary' | 'success' | 'warning' | 'info';
  features: string[];
}

const accentConfig = {
  primary: {
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: 'border-primary/30',
    activeBg: 'bg-primary/5',
    arrowBg: 'bg-primary',
    badge: 'badge-primary',
  },
  success: {
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
    border: 'border-success/30',
    activeBg: 'bg-success/5',
    arrowBg: 'bg-success',
    badge: 'badge-success',
  },
  warning: {
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    border: 'border-warning/30',
    activeBg: 'bg-warning/5',
    arrowBg: 'bg-warning',
    badge: 'badge-warning',
  },
  info: {
    iconBg: 'bg-info/10',
    iconColor: 'text-info',
    border: 'border-info/30',
    activeBg: 'bg-info/5',
    arrowBg: 'bg-info',
    badge: 'badge-info',
  },
};

export default function RoleCard({
  id, role, subtitle, description, href, iconName, accentColor, features
}: RoleCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const cfg = accentConfig[accentColor];

  const handleClick = () => {
    setIsLoading(true);
    // Backend integration point: validate role route availability from Control config
    setTimeout(() => {
      router.push(href);
    }, 400);
  };

  return (
    <button
      id={id}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isLoading}
      className={`
        role-card-hover w-full text-left rounded-2xl border bg-card p-6 cursor-pointer
        transition-all duration-200 active:scale-[0.98]
        ${isHovered ? `${cfg.border} ${cfg.activeBg} shadow-card-lg` : 'border-border shadow-card'}
        ${isLoading ? 'opacity-70 cursor-wait' : ''}
        focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
      `}
      aria-label={`Sign in as ${role} — ${subtitle}`}
    >
      {/* Icon + role badge */}
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${cfg.iconBg} transition-colors duration-200`}>
          <Icon name={iconName as any} size={24} className={cfg.iconColor} />
        </div>
        <span className={`text-[10px] font-700 uppercase tracking-widest px-2 py-1 rounded-full ${cfg.badge}`}>
          {role}
        </span>
      </div>

      {/* Text */}
      <div className="mb-4">
        <h3 className="text-base font-700 text-foreground mb-0.5">{role}</h3>
        <p className="text-[12px] font-500 text-muted-foreground mb-2">{subtitle}</p>
        <p className="text-[13px] text-muted-foreground leading-relaxed">{description}</p>
      </div>

      {/* Features */}
      <ul className="space-y-1.5 mb-5">
        {features.map((feature) => (
          <li key={`${id}-feature-${feature}`} className="flex items-center gap-2">
            <Check size={11} className={cfg.iconColor} />
            <span className="text-[12px] text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className={`flex items-center justify-between pt-4 border-t border-border`}>
        <span className="text-[13px] font-600 text-foreground">
          {isLoading ? 'Redirecting...' : `Sign in as ${role}`}
        </span>
        <div className={`w-7 h-7 rounded-full ${cfg.arrowBg} flex items-center justify-center transition-transform duration-200 ${isHovered ? 'translate-x-0.5' : ''}`}>
          {isLoading ? (
            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <ArrowRight size={14} className="text-white" />
          )}
        </div>
      </div>
    </button>
  );
}